/**
 * TOSS Web Application — initials for a legal entity
 *
 * Two letters standing for a company name, for the marker beside it wherever a company is named:
 * the portal's legal-entity switcher, «Работаю от имени» in «Личные данные», and — since
 * 27.08.2026 — the «Клиент» card on the dispatcher's application page.
 *
 * It started in the portal (`components/layout/client-nav/clientCompanyInitials.ts`) and moved
 * here when the internal application needed it too: a helper reached for by both audiences does
 * not live inside one of them. The old path re-exports, exactly as `formatRelativeAge` did on
 * 26.08.2026.
 */

/** Legal forms that carry no identity — «ООО «Евросиб»» has to initialise as «Е», not «О». */
const LEGAL_FORM_WORDS = new Set(['ооо', 'оао', 'зао', 'пао', 'ао', 'ип', 'нао', 'тоо']);

/**
 * Up to two letters standing for a company name.
 *
 * Quotes and the legal form are dropped first: what tells «ООО «Евросиб»» from
 * «ООО «Евротранс»» is the second word, and initialising the first would give every row on
 * the list the same letter.
 */
export function companyInitials(name: string): string {
  // Hyphens split too: «Транс-Логистик» and «ТрансКонтейнер» in one list would otherwise
  // both initialise as «ТР».
  const words = name
    .replace(/[«»"'`]/g, ' ')
    .split(/[\s-]+/)
    .filter((word) => word.length > 0 && !LEGAL_FORM_WORDS.has(word.toLowerCase()));

  if (words.length === 0) return name.trim().slice(0, 1).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}
